import type React from 'react'
import { useMemo, useState } from 'react'
import type { IProjectSample, ProjectOrder } from '../../../Model/Project/IProjectSample.ts'
import { GroupSection } from './ProjectSampleTableGroup.tsx'
import type { SampleBucket } from '../../Component/SampleGrouping/OperationTypes.js'
import { GroupingPanel } from '../../Component/SampleGrouping/UI/GroupingPanel.js'
import type { VisualizationSampleContainer } from '../../Component/SampleGrouping/VisualizationSampleContainer.js'

type SampleTableProps = {
  data: IProjectSample[] | 'LOADING'
  editing: boolean
  projectId: string
  selectedSamples: number[]
  onSelectionChange: (selectedIds: number[]) => void
  orders: ProjectOrder[]
}

export const ProjectSampleTable: React.FC<SampleTableProps> = ({
  data,
  editing,
  projectId,
  orders,
  selectedSamples,
  onSelectionChange
}) => {
  const [groupedBuckets, setGroupedBuckets] = useState<SampleBucket[]>([])

  const mappedOrders = orders.reduce(
    (previous, current) => {
      previous[current.anonymousOrder.id] = current
      return previous
    },
    {} as Record<string, ProjectOrder>
  )

  const fileSamples: VisualizationSampleContainer[] = useMemo(() => {
    if (data === 'LOADING') return []
    return data.map((d) => ({
      ...d.sample,
      projectSample: d,
      baseOrder: d.sample.orderId ? mappedOrders[d.sample.orderId]?.anonymousOrder : undefined,
      order: d.sample.orderId ? mappedOrders[d.sample.orderId]?.fullOrder : undefined
    }))
  }, [data])

  // Map sample ID -> IProjectSample
  const sampleMap = useMemo(() => {
    if (data === 'LOADING') return new Map<string, IProjectSample>()
    return new Map(data.map((s) => [s.sampleId, s]))
  }, [data])

  // Grouped and enriched IProjectSample buckets
  const enrichedBuckets = useMemo(() => {
    return groupedBuckets.map((bucket) => ({
      label: bucket.label,
      samples: bucket.samples.map((s) => sampleMap.get(s.id)).filter(Boolean) as IProjectSample[]
    }))
  }, [groupedBuckets, sampleMap])

  return (
    <div className="p-4 w-full flex flex-col gap-4">
      {/* Filter + Grouping */}
      <div className="flex items-center justify-between">
        <GroupingPanel samples={fileSamples} mode={'compact'} onBucketsChange={(buckets) => setGroupedBuckets(buckets)} />
      </div>

      {/* Table */}
      <div className="border rounded-sm overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="table w-full">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {editing && <th className={'px-1'}>Select</th>}
              <th>Amplicon</th>
              <th>Sample Name</th>
              <th>Order</th>
              <th>Feature Count</th>
              <th>Added By</th>
              <th>Added Date</th>
            </tr>
          </thead>
          <tbody>
            {data !== 'LOADING' ? (
              enrichedBuckets.map((group) => (
                <GroupSection
                  orders={mappedOrders}
                  key={group.label}
                  label={group.label}
                  samples={group.samples}
                  editing={editing}
                  projectId={projectId}
                  selectedSamples={selectedSamples}
                  onSelectionChange={onSelectionChange}
                />
              ))
            ) : (
              <tr>
                <td colSpan={editing ? 7 : 6}>
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <span className="loading loading-spinner loading-lg mb-4"></span>
                    <span className="text-lg">Loading project samples…</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
